import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Upload, 
  Edit, 
  Trash2, 
  Grid3X3,
  List,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Eye
} from 'lucide-react';
import PokemonEditModal from './modals/PokemonEditModal';
import PokemonAddModal from './modals/PokemonAddModal';
import { getPokemonImageUrl, getPokemonImageSrcList } from '../../utils/imageUtils';
import PokemonDetailModal from './modals/PokemonDetailModal';
import { getApiBaseUrl } from '../../utils/apiUrl';

const PokemonManagement = () => {
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedPokemonIds, setSelectedPokemonIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const [notification, setNotification] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPokemon, setSelectedPokemon] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchPokemon = useCallback(async (page = 1, search = '', type = '', sortBy = 'id', sortOrder = 'asc') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.itemsPerPage.toString(),
        search,
        type,
        sortBy,
        sortOrder
      });

      const response = await fetch(`${getApiBaseUrl()}/api/pokemon?${params}`);
      const data = await response.json();

      if (data.success) {
        setPokemon(data.data.pokemon || data.data);
        setPagination(data.data.pagination || {
          currentPage: page,
          totalPages: Math.ceil((data.data.pokemon || data.data).length / pagination.itemsPerPage),
          totalItems: (data.data.pokemon || data.data).length,
          itemsPerPage: pagination.itemsPerPage
        });
      } else {
        setError(data.error || 'Failed to fetch Pokemon');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.itemsPerPage]);

  // Debounce search term with minimum length
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if term is empty or has at least 3 characters
      if (searchTerm.length === 0 || searchTerm.length >= 3) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchPokemon(pagination.currentPage, debouncedSearchTerm, filterType, sortBy, sortOrder);
  }, [pagination.currentPage, debouncedSearchTerm, filterType, sortBy, sortOrder, fetchPokemon]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFilter = (type) => {
    setFilterType(type);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // const handleSort = (field) => {
  //   if (sortBy === field) {
  //     setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortBy(field);
  //     setSortOrder('asc');
  //   }
  // };

  const handleSelectPokemon = (id) => {
    setSelectedPokemonIds(prev => 
      prev.includes(id) 
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPokemonIds.length === pokemon.length) {
      setSelectedPokemonIds([]);
    } else {
      setSelectedPokemonIds(pokemon.map(p => p.Pokemon_ID));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('คุณแน่ใจหรือไม่ที่จะลบโปเกมอนนี้?')) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/pokemon/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          showNotification('ลบโปเกมอนสำเร็จ', 'success');
          fetchPokemon(pagination.currentPage, searchTerm, filterType, sortBy, sortOrder);
        } else {
          showNotification('ลบโปเกมอนไม่สำเร็จ', 'error');
        }
      } catch (error) {
        showNotification('เกิดข้อผิดพลาดในการลบ', 'error');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPokemonIds.length === 0) return;
    
    if (window.confirm(`คุณแน่ใจหรือไม่ที่จะลบโปเกมอน ${selectedPokemonIds.length} ตัว?`)) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/pokemon/bulk-delete`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ids: selectedPokemonIds })
        });

        if (response.ok) {
          showNotification(`ลบโปเกมอน ${selectedPokemonIds.length} ตัวสำเร็จ`, 'success');
          setSelectedPokemonIds([]);
          fetchPokemon(pagination.currentPage, searchTerm, filterType, sortBy, sortOrder);
        } else {
          showNotification('ลบโปเกมอนไม่สำเร็จ', 'error');
        }
      } catch (error) {
        showNotification('เกิดข้อผิดพลาดในการลบ', 'error');
      }
    }
  };

  const handleExport = () => {
    // Implement export functionality
    showNotification('กำลังส่งออกข้อมูล...', 'success');
  };

  const handleImport = () => {
    // Implement import functionality
    showNotification('กำลังนำเข้าข้อมูล...', 'success');
  };

  const handleEdit = (pokemonId) => {
    const pokemonToEdit = pokemon.find(p => p.Pokemon_ID === pokemonId);
    if (pokemonToEdit) {
      setSelectedPokemon(pokemonToEdit);
      setShowEditModal(true);
    }
  };

  const handleAddPokemon = () => {
    setShowAddModal(true);
  };

  const handleViewDetail = (pokemonId) => {
    const pokemonToView = pokemon.find(p => p.Pokemon_ID === pokemonId);
    if (pokemonToView) {
      setSelectedPokemon(pokemonToView);
      setShowDetailModal(true);
    }
  };

  const handleSavePokemon = async (pokemonData) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pokemonData)
      });

      if (response.ok) {
        showNotification('เพิ่มโปเกมอนสำเร็จ', 'success');
        fetchPokemon(pagination.currentPage, searchTerm, filterType, sortBy, sortOrder);
      } else {
        showNotification('เพิ่มโปเกมอนไม่สำเร็จ', 'error');
      }
    } catch (error) {
      showNotification('เกิดข้อผิดพลาดในการเพิ่มโปเกมอน', 'error');
    }
  };

  const handleUpdatePokemon = async (pokemonData) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon/${pokemonData.Pokemon_ID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pokemonData)
      });

      if (response.ok) {
        showNotification('แก้ไขโปเกมอนสำเร็จ', 'success');
        fetchPokemon(pagination.currentPage, searchTerm, filterType, sortBy, sortOrder);
      } else {
        showNotification('แก้ไขโปเกมอนไม่สำเร็จ', 'error');
      }
    } catch (error) {
      showNotification('เกิดข้อผิดพลาดในการแก้ไขโปเกมอน', 'error');
    }
  };

  const PokemonCard = ({ pokemon }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 ${
        selectedPokemonIds.includes(pokemon.Pokemon_ID) ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedPokemonIds.includes(pokemon.Pokemon_ID)}
            onChange={() => handleSelectPokemon(pokemon.Pokemon_ID)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div 
            className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleEdit(pokemon.Pokemon_ID)}
          >
            <img
              src={getPokemonImageUrl(pokemon.Pokemon_ID)}
              alt={pokemon.Pokemon_Name_EN}
              className="w-12 h-12 object-contain"
              onError={(e) => {
                const sources = getPokemonImageSrcList(pokemon.Pokemon_ID);
                const currentIndex = sources.indexOf(e.target.src);
                if (currentIndex < sources.length - 1) {
                  e.target.src = sources[currentIndex + 1];
                }
              }}
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetail(pokemon.Pokemon_ID)}
            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
            title="ดูรายละเอียด"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEdit(pokemon.Pokemon_ID)}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            title="แก้ไข"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(pokemon.Pokemon_ID)}
            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            title="ลบ"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <h3 
          className="font-bold text-lg text-gray-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
          onClick={() => handleEdit(pokemon.Pokemon_ID)}
        >
          {pokemon.Pokemon_Name_TH}
        </h3>
        <p 
          className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
          onClick={() => handleEdit(pokemon.Pokemon_ID)}
        >
          {pokemon.Pokemon_Name_EN}
        </p>
        <div className="flex flex-wrap gap-1">
          {pokemon.types?.split(',').map((type, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
            >
              {type.trim()}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">HP</div>
            <div className="text-gray-600 dark:text-gray-400">{pokemon.HP}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">Attack</div>
            <div className="text-gray-600 dark:text-gray-400">{pokemon.Attack}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">Defense</div>
            <div className="text-gray-600 dark:text-gray-400">{pokemon.Defense}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const PokemonRow = ({ pokemon }) => (
    <motion.tr
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
        selectedPokemonIds.includes(pokemon.Pokemon_ID) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
    >
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedPokemonIds.includes(pokemon.Pokemon_ID)}
          onChange={() => handleSelectPokemon(pokemon.Pokemon_ID)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center space-x-3">
          <img
            src={getPokemonImageUrl(pokemon.Pokemon_ID)}
            alt={pokemon.Pokemon_Name_EN}
            className="w-10 h-10 object-contain cursor-pointer hover:scale-110 transition-transform duration-200"
            onClick={() => handleEdit(pokemon.Pokemon_ID)}
            onError={(e) => {
              const sources = getPokemonImageSrcList(pokemon.Pokemon_ID);
              const currentIndex = sources.indexOf(e.target.src);
              if (currentIndex < sources.length - 1) {
                e.target.src = sources[currentIndex + 1];
              }
            }}
          />
          <div>
            <div 
              className="font-semibold text-gray-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
              onClick={() => handleEdit(pokemon.Pokemon_ID)}
            >
              {pokemon.Pokemon_Name_TH}
            </div>
            <div 
              className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
              onClick={() => handleEdit(pokemon.Pokemon_ID)}
            >
              {pokemon.Pokemon_Name_EN}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {pokemon.types?.split(',').map((type, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
            >
              {type.trim()}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-center">{pokemon.HP}</td>
      <td className="px-4 py-3 text-center">{pokemon.Attack}</td>
      <td className="px-4 py-3 text-center">{pokemon.Defense}</td>
      <td className="px-4 py-3 text-center font-semibold">{pokemon.Max_CP_Lv50}</td>
      <td className="px-4 py-3">
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetail(pokemon.Pokemon_ID)}
            className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors"
            title="ดูรายละเอียด"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEdit(pokemon.Pokemon_ID)}
            className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
            title="แก้ไข"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(pokemon.Pokemon_ID)}
            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
            title="ลบ"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </motion.tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">เกิดข้อผิดพลาด</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => fetchPokemon()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
        <motion.div
            initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-75"
            >
              <X className="w-4 h-4" />
            </button>
        </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">จัดการโปเกมอน</h1>
          <p className="text-gray-600 dark:text-gray-400">จัดการข้อมูลโปเกมอนทั้งหมดในระบบ</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleImport}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>นำเข้า</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>ส่งออก</span>
          </button>
          <button
            onClick={handleAddPokemon}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>เพิ่มโปเกมอน</span>
          </button>
        </div>
      </div>

        {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                placeholder="ค้นหาโปเกมอน... (พิมพ์อย่างน้อย 3 ตัวอักษร)"
                value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  searchTerm.length > 0 && searchTerm.length < 3 
                    ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                />
                {searchTerm.length > 0 && searchTerm.length < 3 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  พิมพ์อย่างน้อย 3 ตัวอักษรเพื่อค้นหา
                </p>
              )}
            </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span>ตัวกรอง</span>
            </button>

            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-600' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-600' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            </div>
          </div>

        {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
              >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ประเภท
                  </label>
                    <select
                    value={filterType}
                    onChange={(e) => handleFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="Fire">Fire</option>
                    <option value="Water">Water</option>
                    <option value="Grass">Grass</option>
                    <option value="Electric">Electric</option>
                    <option value="Psychic">Psychic</option>
                    <option value="Ice">Ice</option>
                    <option value="Dragon">Dragon</option>
                    <option value="Dark">Dark</option>
                    <option value="Fairy">Fairy</option>
                    </select>
                  </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    เรียงตาม
                  </label>
                    <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="id-asc">ID (น้อยไปมาก)</option>
                    <option value="id-desc">ID (มากไปน้อย)</option>
                    <option value="name_en-asc">ชื่อ (A-Z)</option>
                    <option value="name_en-desc">ชื่อ (Z-A)</option>
                    <option value="max_cp-asc">CP (น้อยไปมาก)</option>
                    <option value="max_cp-desc">CP (มากไปน้อย)</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
      </div>

        {/* Bulk Actions */}
        {selectedPokemonIds.length > 0 && (
          <motion.div
          initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
          >
            <div className="flex items-center justify-between">
            <span className="text-blue-800 dark:text-blue-200 font-medium">
              เลือกแล้ว {selectedPokemonIds.length} รายการ
              </span>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                ลบที่เลือก
                </button>
              <button
                onClick={() => setSelectedPokemon([])}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                ยกเลิก
                </button>
              </div>
            </div>
          </motion.div>
        )}

      {/* Content */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence>
            {pokemon.map((pokemon) => (
              <PokemonCard key={pokemon.Pokemon_ID} pokemon={pokemon} />
            ))}
          </AnimatePresence>
                        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                      checked={selectedPokemonIds.length === pokemon.length && pokemon.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                    โปเกมอน
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                    ประเภท
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                    HP
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                    Attack
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                    Defense
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                    Max CP
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                    การดำเนินการ
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {pokemon.map((pokemon) => (
                    <PokemonRow key={pokemon.Pokemon_ID} pokemon={pokemon} />
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
                      </div>
                    </div>
      )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            แสดง {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} ถึง{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} จาก{' '}
            {pagination.totalItems} รายการ
              </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ก่อนหน้า
            </button>
            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
              หน้า {pagination.currentPage} จาก {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ถัดไป
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <PokemonEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPokemon(null);
        }}
        pokemon={selectedPokemon}
        onSave={handleUpdatePokemon}
      />

      <PokemonAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSavePokemon}
      />

      <PokemonDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedPokemon(null);
        }}
        pokemon={selectedPokemon}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default PokemonManagement;